import { useState, useEffect } from "react";
import { useParams, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2 } from "lucide-react";
import { useBreadcrumb, BreadcrumbItem } from "@/contexts/BreadcrumbContext";

interface Lead {
  _id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export function ContactLayout() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const isActivity = location.pathname.endsWith("/activity");

  const handleDeleteContact = async () => {
    if (!id) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this contact? This action cannot be undone."
    );
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      const response = await fetch("/api/leads", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ leadIds: [id] }),
      });

      if (response.ok) {
        navigate("/contacts");
      } else {
        const data = await response.json();
        console.error("Failed to delete lead:", data.error);
        alert(data.error || "Failed to delete contact");
      }
    } catch (error) {
      console.error("Failed to delete lead:", error);
      alert("An error occurred while deleting the contact");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    async function fetchLead() {
      if (!id) return;

      try {
        const response = await fetch(`/api/leads/${id}`);
        if (response.ok) {
          const data = await response.json();
          const leadData = data.lead;
          setLead(leadData);

          const leadName = leadData.firstName || leadData.lastName
            ? `${leadData.firstName || ""} ${leadData.lastName || ""}`.trim()
            : leadData.email || "Contact";
          const breadcrumbs: BreadcrumbItem[] = [
            { label: "Contacts", path: "/contacts" },
            { label: leadName, path: `/contacts/${id}/edit` },
          ];

          setBreadcrumbs(breadcrumbs);
        } else {
          console.error("Failed to fetch lead");
        }
      } catch (error) {
        console.error("Failed to fetch lead:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchLead();

    return () => {
      clearBreadcrumbs();
    };
  }, [id]);

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!lead) {
    return <div className="p-4">Contact not found</div>;
  }

  return (
    <div className="p-4">
      <Tabs value={isActivity ? "activity" : "edit"} className="w-full">
        <div className="flex items-center justify-between gap-4 mb-4">
          <TabsList>
            <TabsTrigger asChild value="edit">
              <Link to={`/contacts/${id}/edit`}>Edit</Link>
            </TabsTrigger>
            <TabsTrigger asChild value="activity">
              <Link to={`/contacts/${id}/activity`}>Activity</Link>
            </TabsTrigger>
          </TabsList>
          {!isActivity && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={handleDeleteContact}
              disabled={isDeleting}
              aria-label={isDeleting ? "Deleting contact" : "Delete contact"}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
        <Outlet context={{ lead }} />
      </Tabs>
    </div>
  );
}
